import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAlertTriangle,
  LucideCircleCheck,
  LucideCircleX,
  LucideClock,
  LucideDynamicIcon,
  LucideEye,
  LucideEyeOff,
  LucideFlag,
  LucideLock,
  LucideScale,
  provideLucideIcons,
} from '@lucide/angular';

export type UiBadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'brand' | 'muted';
export type UiBadgeSize = 'sm' | 'md';
export type UiBadgeIcon =
  | 'eye'
  | 'eye-off'
  | 'lock'
  | 'flag'
  | 'circle-check'
  | 'clock'
  | 'alert-triangle'
  | 'scale'
  | 'circle-x';

@Component({
  selector: 'ui-badge',
  standalone: true,
  imports: [CommonModule, LucideDynamicIcon],
  providers: [
    provideLucideIcons(
      LucideEye,
      LucideEyeOff,
      LucideLock,
      LucideFlag,
      LucideCircleCheck,
      LucideClock,
      LucideAlertTriangle,
      LucideScale,
      LucideCircleX,
    ),
  ],
  template: `
    <span
      class="ui-badge"
      [class.ui-badge--sm]="size() === 'sm'"
      [class.ui-badge--default]="variant() === 'default'"
      [class.ui-badge--success]="variant() === 'success'"
      [class.ui-badge--warning]="variant() === 'warning'"
      [class.ui-badge--danger]="variant() === 'danger'"
      [class.ui-badge--brand]="variant() === 'brand'"
      [class.ui-badge--muted]="variant() === 'muted'">
      @if (icon()) {
        <svg
          class="ui-badge__icon"
          [lucideIcon]="icon()!"
          [size]="size() === 'sm' ? 11 : 12"
          strokeWidth="2.25"
          aria-hidden="true"></svg>
      }
      <span class="ui-badge__text"><ng-content /></span>
    </span>
  `,
  styles: [`
    .ui-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      max-width: 100%;
      padding: 0.2rem 0.55rem;
      border-radius: 999px;
      border: 1px solid transparent;
      font-size: 0.6875rem;
      font-weight: 600;
      line-height: 1.2;
      letter-spacing: 0.01em;
      vertical-align: middle;
      white-space: nowrap;
    }

    .ui-badge--sm {
      padding: 0.15rem 0.45rem;
      font-size: 0.625rem;
      gap: 0.25rem;
    }

    .ui-badge__icon {
      flex-shrink: 0;
      opacity: 0.92;
    }

    .ui-badge__text {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ui-badge--default {
      color: var(--text-secondary, #595959);
      background: var(--surface-muted, #f5f5f5);
      border-color: var(--border-color, #e8e8e8);
    }

    .ui-badge--muted {
      color: var(--text-muted, #8c8c8c);
      background: color-mix(in srgb, var(--surface-muted, #f5f5f5) 88%, var(--surface-bg));
      border-color: color-mix(in srgb, var(--border-color, #e8e8e8) 85%, transparent);
    }

    .ui-badge--muted .ui-badge__icon {
      color: var(--text-muted, #8c8c8c);
    }

    .ui-badge--success {
      color: var(--success, #52c41a);
      background: color-mix(in srgb, var(--success, #52c41a) 12%, var(--surface-bg));
      border-color: color-mix(in srgb, var(--success, #52c41a) 32%, var(--border-color));
    }

    .ui-badge--success .ui-badge__icon {
      color: var(--success, #52c41a);
    }

    .ui-badge--warning {
      color: var(--warning, #faad14);
      background: color-mix(in srgb, var(--warning, #faad14) 14%, var(--surface-bg));
      border-color: color-mix(in srgb, var(--warning, #faad14) 34%, var(--border-color));
    }

    .ui-badge--warning .ui-badge__icon {
      color: var(--warning, #faad14);
    }

    .ui-badge--danger {
      color: var(--danger, #ff4d4f);
      background: color-mix(in srgb, var(--danger, #ff4d4f) 12%, var(--surface-bg));
      border-color: color-mix(in srgb, var(--danger, #ff4d4f) 32%, var(--border-color));
    }

    .ui-badge--danger .ui-badge__icon {
      color: var(--danger, #ff4d4f);
    }

    .ui-badge--brand {
      color: var(--brand-primary, #1677ff);
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 9%, var(--surface-bg));
      border-color: color-mix(in srgb, var(--brand-primary, #1677ff) 24%, var(--border-color, #e8e8e8));
    }

    .ui-badge--brand .ui-badge__icon {
      color: var(--brand-primary, #1677ff);
    }
  `],
})
export class UiBadgeComponent {
  variant = input<UiBadgeVariant>('default');
  size = input<UiBadgeSize>('md');
  icon = input<UiBadgeIcon | null>(null);
}
